import React, { useState, useEffect } from 'react';
import PatientTimeline from './PatientTimeline';
import PatientAiSummary from './PatientAiSummary';
import PatientDocuments from './PatientDocuments';
import IntakeQRCode from './IntakeQRCode';
import SendNotificationModal from './SendNotificationModal';
import api from '../services/api';
import PaymentMethodsPanel from './PaymentMethodsPanel';

const STATUS_COLORS = {
  scheduled: { bg: '#dbeafe', text: '#1e40af', label: 'Scheduled' },
  completed: { bg: '#dcfce7', text: '#166534', label: 'Completed' },
  'no-show': { bg: '#fef3c7', text: '#92400e', label: 'No-Show' },
  'checked-in': { bg: '#cffafe', text: '#155e75', label: 'Checked In' },
  canceled: { bg: '#fee2e2', text: '#991b1b', label: 'Canceled' },
  rescheduled: { bg: '#e0e7ff', text: '#3730a3', label: 'Rescheduled' },
  open: { bg: '#f3f4f6', text: '#6b7280', label: 'Open' }
};

function formatApptDate(dateStr) {
  if (!dateStr) return '—';
  // Parse date portion directly to avoid timezone shift (midnight UTC → previous day in US timezones)
  const parts = String(dateStr).split('T')[0].split('-').map(Number);
  if (parts.length === 3 && parts[0] > 1900) {
    const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 12, 0, 0));
    return d.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

// Patient DOB is stored as YYYY-MM-DD text — render as MM/DD/YYYY for staff
// consumption (matches how it shows in athena and on patient-facing forms).
function formatDob(dob) {
  if (!dob) return '—';
  const s = String(dob).split('T')[0];
  const parts = s.split('-');
  if (parts.length !== 3) return s;
  const [y, m, d] = parts;
  return `${m}/${d}/${y}`;
}

function formatApptTime(dateStr, startTime) {
  if (startTime) {
    const [hours, minutes] = startTime.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    return `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`;
  }
  // appointment_date is DATE → UTC midnight on the wire. Don't fake a time from
  // it; show "—" until start_time gets populated by the next sync.
  return '—';
}

function AppointmentDetailPanel({ appointment, practiceId, onClose, onAppointmentUpdated }) {
  const [activeTab, setActiveTab] = useState('details');
  const [editingPatient, setEditingPatient] = useState(false);
  const [editPatientData, setEditPatientData] = useState({});
  const [savingPatient, setSavingPatient] = useState(false);
  const [checkinLoading, setCheckinLoading] = useState(false);

  // GH-36 — manual "Transfer Intake Data" picker state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferSources, setTransferSources] = useState(null); // null = not loaded, [] = no sources, [...] = sources
  const [transferSelectedId, setTransferSelectedId] = useState(null);
  const [transferring, setTransferring] = useState(false);

  // Ad-hoc Send SMS / Send Email modal — channel is 'sms' | 'email' | null
  const [sendChannel, setSendChannel] = useState(null);
  // GH-114: intent='meeting_link' for the Meeting SMS/Email buttons (only
  // shown on telemed appointments). Backend's renderPreview branches on
  // this to synthesize a telehealth_link-focused body instead of using
  // the practice's APPOINTMENT_CONFIRMATION template.
  const [sendIntent, setSendIntent] = useState(null);
  const openSend = (ch, intent = null) => {
    setSendChannel(ch);
    setSendIntent(intent);
  };
  // Telemed detection — both "Telemed" and "Telehealth" variants seen in
  // practice templates ("Follow-Up Telemed 30", "Telehealth Visit", etc.)
  const isTelemed = !!(appointment.appointment_type || '').match(/telemed|telehealth/i);

  const handleCheckin = async () => {
    if (!window.confirm('Check in this patient?')) return;
    setCheckinLoading(true);
    try {
      await api.post(`/practices/${practiceId}/appointments/${appointment.id}/checkin`);
      if (onAppointmentUpdated) onAppointmentUpdated();
    } catch (err) {
      // The api.js wrapper attaches the JSON body as `err.body` (fetch-style),
      // not `err.response.data` (axios-style). Check `err.body` first so the
      // ATHENA_SCOPE_NOT_APPROVED friendly path actually fires — without this,
      // every error fell through to the raw technical message.
      const msg = err.body?.error || err.response?.data?.error || err.message;
      if ((err.body?.code || err.response?.data?.code) === 'ATHENA_SCOPE_NOT_APPROVED') {
        alert('Check-in requires Athena Go-Live approval for this practice. Contact admin to enable.');
      } else {
        alert('Check-in failed: ' + msg);
      }
    } finally {
      setCheckinLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!window.confirm('Check out this patient?')) return;
    setCheckinLoading(true);
    try {
      await api.post(`/practices/${practiceId}/appointments/${appointment.id}/checkout`);
      if (onAppointmentUpdated) onAppointmentUpdated();
    } catch (err) {
      // The api.js wrapper attaches the JSON body as `err.body` (fetch-style),
      // not `err.response.data` (axios-style). Check `err.body` first so the
      // ATHENA_SCOPE_NOT_APPROVED friendly path actually fires — without this,
      // every error fell through to the raw technical message.
      const msg = err.body?.error || err.response?.data?.error || err.message;
      if ((err.body?.code || err.response?.data?.code) === 'ATHENA_SCOPE_NOT_APPROVED') {
        alert('Check-out requires Athena Go-Live approval for this practice. Contact admin to enable.');
      } else {
        alert('Check-out failed: ' + msg);
      }
    } finally {
      setCheckinLoading(false);
    }
  };

  // GH-36 — Open the picker, lazy-load eligible source appointments
  const handleTransferOpen = async () => {
    setTransferOpen(true);
    setTransferSources(null);
    setTransferSelectedId(null);
    try {
      const res = await api.getTransferableSources(practiceId, appointment.id);
      setTransferSources(res?.data || []);
    } catch (err) {
      setTransferSources([]);
      alert('Failed to load eligible source appointments: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleTransferConfirm = async () => {
    if (!transferSelectedId) return;
    const src = (transferSources || []).find(s => s.id === transferSelectedId);
    const confirmMsg = `Transfer intake data from the ${src?.appointment_date?.slice(0, 10) || 'selected'} appointment (${src?.completion_pct ?? 0}% complete) onto this one?\n\nAny existing answers on this appointment will be replaced.`;
    if (!window.confirm(confirmMsg)) return;
    setTransferring(true);
    try {
      await api.transferIntakeData(practiceId, appointment.id, transferSelectedId);
      setTransferOpen(false);
      if (onAppointmentUpdated) onAppointmentUpdated();
      alert(`Intake data transferred. The patient won't have to re-fill the form.`);
    } catch (err) {
      alert('Transfer failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setTransferring(false);
    }
  };

  const handleCancel = async () => {
    const reason = window.prompt('Cancel this appointment? Optional reason (visible in athena):', 'Patient request');
    if (reason === null) return;
    setCheckinLoading(true);
    try {
      await api.post(`/practices/${practiceId}/appointments/${appointment.id}/cancel`, { reason });
      if (onAppointmentUpdated) onAppointmentUpdated();
    } catch (err) {
      // The api.js wrapper attaches the JSON body as `err.body` (fetch-style),
      // not `err.response.data` (axios-style). Check `err.body` first so the
      // ATHENA_SCOPE_NOT_APPROVED friendly path actually fires — without this,
      // every error fell through to the raw technical message.
      const msg = err.body?.error || err.response?.data?.error || err.message;
      if ((err.body?.code || err.response?.data?.code) === 'ATHENA_SCOPE_NOT_APPROVED') {
        alert('Cancellation requires Athena Go-Live approval for this practice. Contact admin to enable.');
      } else {
        alert('Cancel failed: ' + msg);
      }
    } finally {
      setCheckinLoading(false);
    }
  };

  if (!appointment) return null;

  const patientName = appointment.first_name || appointment.last_name
    ? `${appointment.first_name || ''} ${appointment.last_name || ''}`.trim()
    : (appointment.patient_id ? `Patient #${appointment.patient_id}` : 'Open Slot');

  const isOpen = !appointment.patient_id && !appointment.first_name && !appointment.last_name;
  const statusStyle = isOpen ? STATUS_COLORS.open : (STATUS_COLORS[appointment.status] || STATUS_COLORS.scheduled);

  // GH-110 follow-up: any state action (check-in, cancel, check-out, etc.)
  // becomes invalid once the appointment date is in the past. Even with the
  // status mapper fix shipping, defensive guard so a migration/sync glitch
  // can never lead staff to mutate Athena for a months-old visit.
  //
  // 2026-06-19 timezone fix — appointment_date is stored as UTC midnight
  // (e.g., "2026-06-19T00:00:00.000Z"). The naive `new Date(appointment_date)
  // < new Date()` returned true the instant the world ticked past UTC midnight,
  // hiding the buttons for staff still on the appointment day (e.g., 7pm CT
  // = 00:00 UTC next day). Compare the CALENDAR DATE strings instead — stored
  // date as-is + today's date in browser-local time. Same-day appts stay
  // actionable regardless of clock time.
  const isPastAppt = (() => {
    if (!appointment.appointment_date) return false;
    const apptDateStr = String(appointment.appointment_date).slice(0, 10); // "YYYY-MM-DD"
    const now = new Date();
    const todayLocalStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return apptDateStr < todayLocalStr;
  })();

  const tabs = [
    { key: 'details', label: 'Details' },
    ...(appointment.patient_id
      ? [
          { key: 'intake', label: 'Intake' },
          { key: 'documents', label: 'Documents' },
          { key: 'payments', label: 'Payments' },
          { key: 'timeline', label: 'Timeline' },
          { key: 'summary', label: 'AI Summary' }
        ]
      : [])
  ];

  return (
    <div style={styles.panel}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.avatar}>
            {(appointment.first_name || '?')[0]}{(appointment.last_name || '?')[0]}
          </div>
          <div>
            <div style={styles.name}>{patientName}</div>
            <div style={styles.meta}>
              {/* Labels — pill shape (borderRadius: 999) + lighter weight
                  to read clearly as informational badges, not actions.
                  Previously they shared borderRadius:6 + fontWeight:700
                  with the action buttons below and were easy to mistake
                  for clickable controls. */}
              <span style={{
                display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                fontSize: 10.5, fontWeight: 600, letterSpacing: '0.4px',
                textTransform: 'uppercase',
                backgroundColor: statusStyle.bg, color: statusStyle.text
              }}>
                {statusStyle.label}
              </span>
              {appointment.appointment_type && (
                <span style={{
                  display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                  fontSize: 10.5, fontWeight: 600, letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                  background: 'rgba(37,99,235,0.08)', color: '#2563eb',
                }}>
                  {appointment.appointment_type}
                </span>
              )}
              {/* Action buttons — defensive guard: hide Check In / Check
                  Out / Cancel for past appointments regardless of status.
                  Backfills or migration glitches can leave a past visit
                  flagged as 'scheduled' (GH-110 follow-up), and clicking
                  Check In on a Jan visit would create a bogus check-in
                  event in Athena. !isPastAppt blocks that path. */}
              {appointment.status === 'scheduled' && appointment.patient_id && !isPastAppt && (
                <button onClick={handleCheckin} disabled={checkinLoading}
                  style={{ padding: '4px 14px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', background: '#059669', color: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                  {checkinLoading ? 'Checking in...' : 'Check In'}
                </button>
              )}
              {appointment.status === 'checked-in' && !isPastAppt && (
                <button onClick={handleCheckout} disabled={checkinLoading}
                  style={{ padding: '4px 14px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', background: '#7c3aed', color: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                  {checkinLoading ? 'Checking out...' : 'Check Out'}
                </button>
              )}
              {['scheduled', 'checked-in'].includes(appointment.status) && appointment.patient_id && !isPastAppt && (
                <button onClick={handleCancel} disabled={checkinLoading}
                  style={{ padding: '4px 14px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', background: '#dc2626', color: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                  {checkinLoading ? 'Canceling...' : 'Cancel Appointment'}
                </button>
              )}
              {appointment.patient_id && (
                <>
                  <button onClick={() => openSend('sms')}
                    title={appointment.patient_phone ? 'Send a one-off SMS to this patient' : 'Patient has no phone on file'}
                    disabled={!appointment.patient_phone}
                    style={{
                      padding: '4px 14px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600,
                      cursor: appointment.patient_phone ? 'pointer' : 'not-allowed',
                      background: appointment.patient_phone ? '#2563eb' : '#cbd5e1', color: '#fff',
                      boxShadow: appointment.patient_phone ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                    }}>
                    Send SMS
                  </button>
                  <button onClick={() => openSend('email')}
                    title={appointment.patient_email ? 'Send a one-off email to this patient' : 'Patient has no email on file'}
                    disabled={!appointment.patient_email}
                    style={{
                      padding: '4px 14px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600,
                      cursor: appointment.patient_email ? 'pointer' : 'not-allowed',
                      background: appointment.patient_email ? '#0891b2' : '#cbd5e1', color: '#fff',
                      boxShadow: appointment.patient_email ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                    }}>
                    Send Email
                  </button>
                  {/* GH-114: meeting-link send buttons — only shown for
                      telemed appointment types. Reuses the SendNotificationModal
                      with intent='meeting_link' so the body comes pre-filled
                      with the provider's telehealth_link. */}
                  {isTelemed && (
                    <>
                      <button onClick={() => openSend('sms', 'meeting_link')}
                        title={appointment.patient_phone ? 'Send the telemed meeting link via SMS' : 'Patient has no phone on file'}
                        disabled={!appointment.patient_phone}
                        style={{
                          padding: '4px 14px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600,
                          cursor: appointment.patient_phone ? 'pointer' : 'not-allowed',
                          background: appointment.patient_phone ? '#7c3aed' : '#cbd5e1', color: '#fff',
                          boxShadow: appointment.patient_phone ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                        }}>
                        Meeting SMS
                      </button>
                      <button onClick={() => openSend('email', 'meeting_link')}
                        title={appointment.patient_email ? 'Send the telemed meeting link via email' : 'Patient has no email on file'}
                        disabled={!appointment.patient_email}
                        style={{
                          padding: '4px 14px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600,
                          cursor: appointment.patient_email ? 'pointer' : 'not-allowed',
                          background: appointment.patient_email ? '#6d28d9' : '#cbd5e1', color: '#fff',
                          boxShadow: appointment.patient_email ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                        }}>
                        Meeting Email
                      </button>
                    </>
                  )}
                </>
              )}
              {/* GH-36 v3 — only show on appointments that can be intake
                  TARGETS. Previous round showed on every status which let
                  admins open the canceled appointment, transfer the empty
                  auto-gen form INTO it from the rescheduled one, and end up
                  with 0% data on a canceled appt while the real data sat
                  EXPIRED on the original. The Transfer flow's mental model
                  is "copy answers FROM somewhere INTO THIS appointment", so
                  it only makes sense on appts where the patient still needs
                  to fill the intake. Canceled / rescheduled / completed
                  appointments aren't intake targets. */}
              {appointment.patient_id && ['scheduled', 'checked-in'].includes(appointment.status) && !isPastAppt && (
                <button onClick={handleTransferOpen}
                  style={{ padding: '4px 14px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', background: '#0ea5e9', color: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                  Transfer Intake Data
                </button>
              )}
            </div>
          </div>
        </div>
        <button onClick={onClose} style={styles.closeBtn}>&times;</button>
      </div>

      {/* GH-36 — Transfer Intake Data picker modal */}
      {transferOpen && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setTransferOpen(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}>
          <div style={{
            background: '#fff', borderRadius: 12, width: 520, maxWidth: '90vw',
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 18px 48px rgba(15,23,42,0.25)',
          }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Transfer Intake Data into this appointment</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                Pick the appointment to copy answers <strong>from</strong>. Their answers will be carried into this appointment so the patient doesn't re-fill the form.
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 22px' }}>
              {transferSources === null && (
                <div style={{ padding: '24px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading eligible appointments…</div>
              )}
              {transferSources?.length === 0 && (
                <div style={{ padding: '24px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                  No prior appointments with intake data found for this patient.
                </div>
              )}
              {transferSources?.map(src => {
                const dateLabel = src.appointment_date
                  ? new Date(String(src.appointment_date).slice(0, 10) + 'T12:00:00')
                      .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                  : '—';
                const isSelected = transferSelectedId === src.id;
                return (
                  <label key={src.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                    borderRadius: 8, marginTop: 6, cursor: 'pointer',
                    background: isSelected ? '#eff6ff' : '#fff',
                    border: `1px solid ${isSelected ? '#3b82f6' : '#e2e8f0'}`,
                  }}>
                    <input type="radio" name="transfer-src" checked={isSelected}
                      onChange={() => setTransferSelectedId(src.id)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                        {dateLabel}
                        {src.appointment_type ? <span style={{ color: '#64748b', fontWeight: 400 }}> · {src.appointment_type}</span> : null}
                        {src.provider_name ? <span style={{ color: '#64748b', fontWeight: 400 }}> · {src.provider_name}</span> : null}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, display: 'flex', gap: 8 }}>
                        <span style={{ padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {src.status || 'unknown'}
                        </span>
                        <span style={{ padding: '2px 8px', borderRadius: 4, background: '#f0fdf4', color: '#15803d', fontWeight: 600 }}>
                          {Math.round(src.completion_pct || 0)}% complete
                        </span>
                        {src.intake_status && (
                          <span style={{ padding: '2px 8px', borderRadius: 4, background: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }}>
                            {src.intake_status}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setTransferOpen(false)} disabled={transferring}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleTransferConfirm} disabled={!transferSelectedId || transferring}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: transferSelectedId && !transferring ? '#0ea5e9' : '#94a3b8',
                  color: '#fff', fontSize: 13, fontWeight: 600,
                  cursor: transferSelectedId && !transferring ? 'pointer' : 'not-allowed',
                }}>
                {transferring ? 'Transferring…' : 'Transfer Selected Data'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs — Prominent Pill Style */}
      <div style={styles.tabRow}>
        <div style={styles.tabWrapper}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                ...styles.tab,
                ...(activeTab === tab.key ? styles.tabActive : {})
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {activeTab === 'details' && (
          <div>
            <div style={styles.detailGrid}>
              <DetailRow label="Date" value={formatApptDate(appointment.appointment_date)} />
              <DetailRow label="Time" value={formatApptTime(appointment.appointment_date, appointment.start_time)} />
              <DetailRow label="Patient" value={patientName} />
              {appointment.patient_phone && (
                <DetailRow label="Phone" value={appointment.patient_phone} />
              )}
              {appointment.date_of_birth && (
                <DetailRow label="Date of Birth" value={formatDob(appointment.date_of_birth)} />
              )}
              <DetailRow label="Type" value={appointment.appointment_type} />
              <DetailRow label="Provider" value={appointment.provider_name} />
              <DetailRow label="Department" value={appointment.department_name} />
              <DetailRow label="Duration" value={appointment.duration_minutes ? `${appointment.duration_minutes} min` : null} />
              <DetailRow label="Status" value={statusStyle.label} />
              <DetailRow label="Booked By" value={appointment.booked_by} />
              {appointment.athena_patient_id && (
                <DetailRow label="Patient ID (Athena)" value={appointment.athena_patient_id} />
              )}
              {appointment.athena_appointment_id && (
                <DetailRow label="Appt ID (Athena)" value={appointment.athena_appointment_id} />
              )}
              {appointment.notes && (
                <DetailRow label="Notes" value={appointment.notes} />
              )}
            </div>

            {/* Payment Methods */}
            {appointment.patient_id && practiceId && (
              <PatientPaymentMethods practiceId={practiceId} patientId={appointment.patient_id} />
            )}

            {/* Edit Patient Info */}
            {appointment.patient_id && (
              <div style={{ marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                {editingPatient ? (
                  <div>
                    <strong style={{ fontSize: 13, color: '#374151' }}>Edit Patient Info</strong>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                      {[
                        { key: 'first_name', label: 'First Name', type: 'text' },
                        { key: 'last_name', label: 'Last Name', type: 'text' },
                        { key: 'phone', label: 'Phone', type: 'tel' },
                        { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
                      ].map(f => (
                        <div key={f.key}>
                          <label style={{ fontSize: 12, color: '#6b7280' }}>{f.label}</label>
                          <input
                            type={f.type}
                            value={editPatientData[f.key] || ''}
                            onChange={e => setEditPatientData({ ...editPatientData, [f.key]: e.target.value })}
                            style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginTop: 2 }}
                          />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button
                        disabled={savingPatient}
                        onClick={async () => {
                          setSavingPatient(true);
                          try {
                            await api.updatePatient(appointment.patient_id, editPatientData);
                            setEditingPatient(false);
                            if (onAppointmentUpdated) onAppointmentUpdated();
                          } catch (err) {
                            alert('Error: ' + err.message);
                          } finally {
                            setSavingPatient(false);
                          }
                        }}
                        style={{ padding: '6px 16px', fontSize: 12, fontWeight: 500, color: '#fff', background: '#3b82f6', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      >
                        {savingPatient ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={() => setEditingPatient(false)} style={{ padding: '6px 16px', fontSize: 12, fontWeight: 500, color: '#374151', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingPatient(true);
                      setEditPatientData({
                        first_name: appointment.first_name || '',
                        last_name: appointment.last_name || '',
                        phone: appointment.patient_phone || '',
                        date_of_birth: appointment.date_of_birth || '',
                      });
                    }}
                    style={{
                      padding: '10px 24px', fontSize: 13, fontWeight: 600,
                      color: '#2563eb', background: 'transparent',
                      border: '1px solid #e2e8f0', borderRadius: 10,
                      cursor: 'pointer', transition: 'all 0.2s',
                    }}
                  >
                    Edit Patient Info
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {activeTab === 'intake' && appointment.patient_id && (
          <IntakeStatusSection practiceId={practiceId} appointment={appointment} />
        )}
        {activeTab === 'documents' && appointment.patient_id && (
          <PatientDocuments practiceId={practiceId} patientId={appointment.patient_id} />
        )}
        {activeTab === 'payments' && appointment.patient_id && (
          <PaymentMethodsPanel patientId={appointment.patient_id} practiceId={practiceId} />
        )}
        {activeTab === 'timeline' && appointment.patient_id && (
          <PatientTimeline practiceId={practiceId} patientId={appointment.patient_id} />
        )}
        {activeTab === 'summary' && appointment.patient_id && (
          <PatientAiSummary practiceId={practiceId} patientId={appointment.patient_id} context="detail" />
        )}
      </div>

      {sendChannel && (
        <SendNotificationModal
          appointment={appointment}
          practiceId={practiceId}
          channel={sendChannel}
          intent={sendIntent}
          onClose={() => { setSendChannel(null); setSendIntent(null); }}
          onSent={() => { if (onAppointmentUpdated) onAppointmentUpdated(); }}
        />
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={styles.detailRow}>
      <div style={styles.detailLabel}>{label}</div>
      <div style={styles.detailValue}>{value || '—'}</div>
    </div>
  );
}

// ─── Patient Payment Methods ────────────────────────────────
const CARD_BRAND_LABELS = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
  diners: 'Diners',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

function PatientPaymentMethods({ practiceId, patientId }) {
  const [methods, setMethods] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!practiceId || !patientId) return;
    setLoading(true);
    api.getPatientPaymentMethods(practiceId, patientId)
      .then(res => setMethods(res.data || []))
      .catch(() => setMethods(null))
      .finally(() => setLoading(false));
  }, [practiceId, patientId]);

  // Don't render if loading, errored, or no methods
  if (loading || methods === null || methods.length === 0) return null;

  return (
    <div style={{ marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
      <strong style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        Payment Methods on File
      </strong>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {methods.map((m, i) => {
          const brand = CARD_BRAND_LABELS[m.card_brand] || (m.card_brand ? m.card_brand.charAt(0).toUpperCase() + m.card_brand.slice(1) : 'Card');
          const expStr = m.card_exp_month && m.card_exp_year
            ? `${String(m.card_exp_month).padStart(2, '0')}/${String(m.card_exp_year).slice(-2)}`
            : null;
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', background: '#f9fafb', borderRadius: 8,
              border: '1px solid #e5e7eb',
            }}>
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none" style={{ flexShrink: 0 }}>
                <rect x="0.5" y="0.5" width="19" height="15" rx="2.5" stroke="#9ca3af" fill="#f9fafb" />
                <rect x="0" y="4" width="20" height="3" fill="#d1d5db" />
                <rect x="3" y="10" width="6" height="2" rx="1" fill="#9ca3af" />
              </svg>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                  {brand}
                </span>
                <span style={{ fontSize: 13, color: '#6b7280', marginLeft: 8, letterSpacing: '0.05em' }}>
                  {'•••• '}{m.card_last_four}
                </span>
              </div>
              {expStr && (
                <span style={{ fontSize: 12, color: '#94a3b8' }}>
                  Exp {expStr}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── EHR Push Status Colors ─────────────────────────────────
const PUSH_STATUS = {
  not_started: { bg: '#f3f4f6', text: '#6b7280', label: 'Not Started' },
  pushing: { bg: '#dbeafe', text: '#1e40af', label: 'Pushing...' },
  completed: { bg: '#dcfce7', text: '#166534', label: 'Completed' },
  partial: { bg: '#fef3c7', text: '#92400e', label: 'Partial' },
  failed: { bg: '#fee2e2', text: '#991b1b', label: 'Failed' },
  success: { bg: '#dcfce7', text: '#166534', label: 'Success' },
  skipped: { bg: '#f3f4f6', text: '#6b7280', label: 'Skipped' },
  pending: { bg: '#f3f4f6', text: '#6b7280', label: 'Pending' },
};

const FORM_STATUS = {
  NOT_STARTED: { bg: '#f3f4f6', text: '#6b7280', label: 'Not Started' },
  OPENED: { bg: '#dbeafe', text: '#1e40af', label: 'Opened' },
  IN_PROGRESS: { bg: '#fef3c7', text: '#92400e', label: 'In Progress' },
  COMPLETED: { bg: '#dcfce7', text: '#166534', label: 'Completed' },
  EXPIRED: { bg: '#fee2e2', text: '#991b1b', label: 'Expired' },
};

function StatusBadge({ map, status }) {
  const s = map[status] || { bg: '#f3f4f6', text: '#6b7280', label: status || '—' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, backgroundColor: s.bg, color: s.text
    }}>
      {s.label}
    </span>
  );
}

// ─── Human-readable labels + icons for API call types ───────
const CALL_TYPE_LABELS = {
  demographics:            { label: 'Demographics',        icon: '👤', phase: 1 },
  medications:             { label: 'Medications',         icon: '💊', phase: 1 },
  allergies:               { label: 'Allergies',           icon: '⚠',  phase: 1 },
  vital_signs:             { label: 'Vital Signs',         icon: '❤',  phase: 1 },
  insurance:               { label: 'Insurance',           icon: '🏥', phase: 1 },
  medical_history:         { label: 'Medical History',     icon: '📋', phase: 1 },
  surgical_history:        { label: 'Surgical History',    icon: '🔪', phase: 1 },
  social_history:          { label: 'Social History',      icon: '🏠', phase: 1 },
  family_history:          { label: 'Family History',      icon: '👨‍👩‍👧', phase: 1 },
  drivers_license:         { label: "Driver's License",    icon: '🪪', phase: 1 },
  insurance_card:          { label: 'Insurance Card',      icon: '💳', phase: 1 },
  patient_signature:       { label: 'Patient Signature',   icon: '✍',  phase: 1 },
  default_pharmacy:        { label: 'Preferred Pharmacy',  icon: '🏪', phase: 1 },
  default_lab:             { label: 'Preferred Lab',       icon: '🔬', phase: 1 },
  care_team:               { label: 'Care Team',           icon: '👥', phase: 1 },
  hpi:                     { label: 'HPI',                 icon: '📝', phase: 2 },
  review_of_systems:       { label: 'Review of Systems',   icon: '🩺', phase: 2 },
  encounter_document:      { label: 'Encounter Document',  icon: '📄', phase: 2 },
  questionnaire_screener:  { label: 'Screener',            icon: '📊', phase: 2 },
  questionnaire_pdf:       { label: 'Questionnaire PDF',   icon: '📄', phase: 2 },
  lab_report:              { label: 'Lab Report',          icon: '🧪', phase: 1 },
};

function getCallLabel(apiCallType, displayName) {
  if (apiCallType?.startsWith('consent_form_')) {
    const idx = parseInt(apiCallType.replace('consent_form_', ''), 10) + 1;
    return { label: displayName || `Consent Form ${idx}`, icon: '📜', phase: 1 };
  }
  if (apiCallType?.startsWith('privacy_notice_')) {
    const idx = parseInt(apiCallType.replace('privacy_notice_', ''), 10) + 1;
    return { label: displayName || `Privacy Notice ${idx}`, icon: '🔒', phase: 1 };
  }
  if (apiCallType?.startsWith('questionnaire_screener_')) {
    const idx = parseInt(apiCallType.replace('questionnaire_screener_', ''), 10) + 1;
    return { label: displayName || `Screener ${idx}`, icon: '📊', phase: 2 };
  }
  return CALL_TYPE_LABELS[apiCallType] || { label: apiCallType, icon: '📦', phase: 1 };
}

// ─── Status indicator dot ───────────────────────────────────
function StatusDot({ status }) {
  const colors = {
    success: '#16a34a', completed: '#16a34a',
    failed: '#dc2626',
    pushing: '#3b82f6',
    partial: '#f59e0b',
    skipped: '#94a3b8',
    pending: '#d1d5db', not_started: '#d1d5db',
  };
  const color = colors[status] || '#d1d5db';
  const isPushing = status === 'pushing';
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      backgroundColor: color, flexShrink: 0,
      animation: isPushing ? 'pulse 1.5s infinite' : 'none',
    }} />
  );
}

function IntakeStatusSection({ practiceId, appointment }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [pushingId, setPushingId] = useState(null);

  const appointmentId = appointment.id;

  const loadStatus = () => {
    if (!practiceId || !appointmentId) return;
    setLoading(true);
    api.get(`/practices/${practiceId}/appointments/${appointmentId}/intake-status`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(loadStatus, [practiceId, appointmentId]);

  const handlePush = async () => {
    if (!data?.ehrPush?.generationLogId) return;
    setActing(true);
    try {
      await api.post(`/practices/${practiceId}/intake/generations/${data.ehrPush.generationLogId}/push-to-ehr`);
      loadStatus();
    } catch (err) {
      alert('Push failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setActing(false);
    }
  };

  const handleRetry = async () => {
    if (!data?.ehrPush?.generationLogId) return;
    setActing(true);
    try {
      await api.post(`/practices/${practiceId}/intake/generations/${data.ehrPush.generationLogId}/retry-ehr-push`);
      loadStatus();
    } catch (err) {
      alert('Retry failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setActing(false);
    }
  };

  const handlePushSingle = async (pushLogId) => {
    if (!data?.ehrPush?.generationLogId) return;
    setPushingId(pushLogId);
    try {
      await api.pushSingleEHR(practiceId, data.ehrPush.generationLogId, pushLogId);
      loadStatus();
    } catch (err) {
      alert('Push failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setPushingId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, color: '#6b7280' }}>
        <span style={{ fontSize: 13 }}>Loading intake status...</span>
      </div>
    );
  }

  if (!data || !data.hasIntake) {
    return (
      <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
        <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>No intake form for this appointment</p>
        <p style={{ color: '#94a3b8', fontSize: 12, margin: '4px 0 0' }}>
          Forms are generated automatically when appointments sync from EHR
        </p>
      </div>
    );
  }

  const { form, ehrPush } = data;
  const timeSpent = form.time_spent_seconds
    ? `${Math.floor(form.time_spent_seconds / 60)}m ${form.time_spent_seconds % 60}s`
    : null;

  // Group EHR calls by phase
  const phase1Calls = [];
  const phase2Calls = [];
  if (ehrPush?.calls) {
    for (const call of ehrPush.calls) {
      const info = getCallLabel(call.api_call_type, call.display_name);
      const enriched = { ...call, ...info };
      if (info.phase === 2) phase2Calls.push(enriched);
      else phase1Calls.push(enriched);
    }
  }

  // Summary counts
  const totalCalls = ehrPush?.calls?.length || 0;
  const successCount = ehrPush?.calls?.filter(c => c.status === 'success').length || 0;
  const failedCount = ehrPush?.calls?.filter(c => c.status === 'failed').length || 0;
  const skippedCount = ehrPush?.calls?.filter(c => c.status === 'skipped').length || 0;

  // Pending-check-in: appointment not yet checked in AND Phase 2 calls were skipped
  // because there was no encounter_id. These will auto-push when EHR Bridge forwards
  // the check-in event (IntakeEHRPushOrchestrator.pushEncounterDataOnCheckIn). Surface
  // this as a positive "queued" state rather than "skipped" so staff don't interpret
  // it as an error.
  //
  // Coupled to the exact reason string emitted in IntakeEHRPushOrchestrator.js at
  // "No encounter_id available". If you change that string on the server, update this
  // match here too (and the orchestrator's retry uses the same SKIP reason to pick
  // rows to retry, so all three places must stay in sync).
  const NO_ENCOUNTER_REASON = 'No encounter_id available';
  const apptStatus = appointment?.status;
  const pendingCheckInCount = (apptStatus === 'scheduled' || apptStatus === 'confirmed')
    ? (ehrPush?.calls?.filter(c => c.status === 'skipped' && (c.error_message || '').includes(NO_ENCOUNTER_REASON)).length || 0)
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── Intake Form Status ────────────────────── */}
      <div style={intakeStyles.card}>
        <div style={intakeStyles.cardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>📋</span>
            <span style={intakeStyles.cardTitle}>Intake Form</span>
          </div>
          <StatusBadge map={FORM_STATUS} status={form.status} />
        </div>

        {/* Progress */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {form.completed_categories != null && form.total_categories != null
                ? `${form.completed_categories} of ${form.total_categories} sections`
                : 'Progress'}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{form.completion_pct || 0}%</span>
          </div>
          <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 3,
              width: `${form.completion_pct || 0}%`,
              background: form.status === 'COMPLETED' ? '#16a34a' : '#3b82f6',
              transition: 'width 0.4s ease'
            }} />
          </div>
        </div>

        {/* Timestamps */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
          {form.opened_at && (
            <span style={intakeStyles.timestamp}>Opened {new Date(form.opened_at).toLocaleString()}</span>
          )}
          {form.completed_at && (
            <span style={{ ...intakeStyles.timestamp, color: '#16a34a' }}>
              Completed {new Date(form.completed_at).toLocaleString()}
            </span>
          )}
          {timeSpent && <span style={intakeStyles.timestamp}>Duration: {timeSpent}</span>}
          {form.is_adhoc && (
            <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 500 }}>Ad-hoc</span>
          )}
        </div>

        {/* QR Code for in-person form filling */}
        {form.access_token && form.status !== 'COMPLETED' && (
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 10, paddingTop: 8 }}>
            <IntakeQRCode accessToken={form.access_token} size={150} />
          </div>
        )}
      </div>

      {/* ── EHR Push Status ────────────────────── */}
      <div style={intakeStyles.card}>
        <div style={intakeStyles.cardHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🔄</span>
            <span style={intakeStyles.cardTitle}>EHR Push</span>
          </div>
          {ehrPush
            ? <StatusBadge map={PUSH_STATUS} status={ehrPush.status} />
            : <span style={{ fontSize: 12, color: '#94a3b8' }}>Waiting</span>
          }
        </div>

        {ehrPush && totalCalls > 0 && (
          <>
            {/* Pending-check-in banner */}
            {pendingCheckInCount > 0 && (
              <div style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 8,
                padding: '8px 12px',
                marginBottom: 10,
                fontSize: 12,
                color: '#1e3a8a',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span>⏳</span>
                <span>
                  <strong>{pendingCheckInCount}</strong> encounter-dependent call{pendingCheckInCount === 1 ? '' : 's'} (HPI, ROS, screeners) will push automatically when this appointment is checked in.
                </span>
              </div>
            )}

            {/* Summary bar */}
            <div style={{
              display: 'flex', gap: 12, padding: '8px 12px', marginBottom: 10,
              background: '#f8fafc', borderRadius: 8, fontSize: 12
            }}>
              <span style={{ color: '#16a34a', fontWeight: 600 }}>{successCount} passed</span>
              {failedCount > 0 && <span style={{ color: '#dc2626', fontWeight: 600 }}>{failedCount} failed</span>}
              {skippedCount > 0 && <span style={{ color: '#94a3b8' }}>{skippedCount} skipped</span>}
              <span style={{ color: '#6b7280', marginLeft: 'auto' }}>{totalCalls} total</span>
            </div>

            {/* Phase 1: Independent calls */}
            {phase1Calls.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={intakeStyles.phaseHeader}>
                  <span>Phase 1 — Patient Data</span>
                  <span style={intakeStyles.phaseSub}>Pushed on form completion</span>
                </div>
                <div style={intakeStyles.callList}>
                  {phase1Calls.map((call, i) => (
                    <EHRCallRow key={i} call={call} onPushSingle={handlePushSingle} pushingId={pushingId} />
                  ))}
                </div>
              </div>
            )}

            {/* Phase 2: Encounter-dependent calls */}
            {phase2Calls.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={intakeStyles.phaseHeader}>
                  <span>Phase 2 — Encounter Data</span>
                  <span style={intakeStyles.phaseSub}>Pushed on check-in (requires encounter)</span>
                </div>
                <div style={intakeStyles.callList}>
                  {phase2Calls.map((call, i) => (
                    <EHRCallRow key={i} call={call} onPushSingle={handlePushSingle} pushingId={pushingId} />
                  ))}
                </div>
              </div>
            )}

            {/* Error details.
                Skipped pre-checkin rows store "No encounter_id available" in
                error_message — that's the SKIP reason, not a failure. We surface
                those above via the pending-check-in banner, so exclude them here
                otherwise staff see informational state painted as red errors. */}
            {ehrPush.calls.some(c => c.error_message && !(c.status === 'skipped' && (c.error_message || '').includes(NO_ENCOUNTER_REASON))) && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
                padding: '8px 12px', marginBottom: 10
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#991b1b', marginBottom: 4 }}>Errors</div>
                {ehrPush.calls
                  .filter(c => c.error_message && !(c.status === 'skipped' && (c.error_message || '').includes(NO_ENCOUNTER_REASON)))
                  .map((c, i) => (
                  <div key={i} style={{ fontSize: 11, color: '#dc2626', padding: '2px 0' }}>
                    <strong>{getCallLabel(c.api_call_type, c.display_name).label}:</strong> {c.error_message}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* No calls yet */}
        {ehrPush && totalCalls === 0 && (
          <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>
            No EHR push calls recorded yet. Push will execute when the form is completed.
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          {ehrPush && (failedCount > 0 || skippedCount > 0) && (
            <button onClick={handleRetry} disabled={acting} style={intakeStyles.btnRetry}>
              {acting ? 'Retrying...' : `Retry ${failedCount > 0 ? 'Failed' : 'Skipped'} (${failedCount + skippedCount})`}
            </button>
          )}
          {ehrPush && !['completed', 'pushing'].includes(ehrPush.status) && (
            <button onClick={handlePush} disabled={acting} style={intakeStyles.btnPush}>
              {acting ? 'Pushing...' : 'Push All to EHR'}
            </button>
          )}
          {!ehrPush && form.status === 'COMPLETED' && (
            <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
              No generation log linked — form completed without EHR integration
            </span>
          )}
        </div>
      </div>

      {/* Pulse animation for pushing status */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

function EHRCallRow({ call, onPushSingle, pushingId }) {
  const canPush = true;
  const isPushing = pushingId === call.id;
  const [expanded, setExpanded] = React.useState(false);

  // Parse LLM data from response_data if available
  let llmData = null;
  if (call.response_data) {
    try {
      const parts = (call.response_data || '').split(' | ');
      for (const part of parts) {
        const parsed = JSON.parse(part);
        if (parsed.llm_output) { llmData = parsed; break; }
      }
    } catch { /* not LLM data */ }
  }

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '5px 10px', borderRadius: 6, fontSize: 12,
        background: call.status === 'failed' ? '#fef2f2' : '#f8fafc',
        cursor: llmData ? 'pointer' : 'default',
      }} onClick={() => llmData && setExpanded(!expanded)}>
        <StatusDot status={isPushing ? 'pushing' : call.status} />
        <span style={{ fontSize: 14, lineHeight: 1 }}>{call.icon}</span>
        {/* GH-19: keep name tight next to the icon so the Push button sits
            immediately after the label instead of being pushed to the far
            right of the row. */}
        <span style={{ color: '#374151', fontWeight: 500 }}>{call.label}</span>
        {canPush && onPushSingle && (
          <button
            onClick={(e) => { e.stopPropagation(); onPushSingle(call.id); }}
            disabled={!!pushingId}
            style={{
              padding: '2px 8px', borderRadius: 4, border: '1px solid #3b82f6',
              background: isPushing ? '#dbeafe' : '#eff6ff', color: '#1e40af',
              fontSize: 11, fontWeight: 600, cursor: pushingId ? 'not-allowed' : 'pointer',
              opacity: pushingId && !isPushing ? 0.5 : 1,
              transition: 'all 0.15s',
            }}
          >
            {isPushing ? 'Pushing...' : 'Push'}
          </button>
        )}
        {/* Spacer takes the remaining horizontal space, pushing AI/attempt
            indicators and the StatusBadge to the right side of the row. */}
        <span style={{ flex: 1 }} />
        {llmData && <span style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600 }}>AI</span>}
        {call.attempt_count > 1 && (
          <span style={{ color: '#94a3b8', fontSize: 10 }}>attempt {call.attempt_count}</span>
        )}
        <StatusBadge map={PUSH_STATUS} status={isPushing ? 'pushing' : call.status} />
      </div>
      {expanded && llmData && (
        <div style={{ margin: '4px 0 8px 28px', padding: 10, background: '#faf5ff', borderRadius: 6, border: '1px solid #e9d5ff', fontSize: 12 }}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 600, color: '#7c3aed', fontSize: 11, marginBottom: 4 }}>Patient Data Sent to AI</div>
            <pre style={{ whiteSpace: 'pre-wrap', color: '#374151', fontSize: 11, lineHeight: 1.5, margin: 0, background: '#fff', padding: 8, borderRadius: 4, border: '1px solid #e5e7eb', maxHeight: 150, overflow: 'auto' }}>{llmData.llm_input}</pre>
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#7c3aed', fontSize: 11, marginBottom: 4 }}>AI Generated Note ({llmData.llm_source})</div>
            <pre style={{ whiteSpace: 'pre-wrap', color: '#1f2937', fontSize: 11, lineHeight: 1.5, margin: 0, background: '#fff', padding: 8, borderRadius: 4, border: '1px solid #e5e7eb' }}>{llmData.llm_output}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

const intakeStyles = {
  card: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12,
  },
  cardTitle: { fontWeight: 600, fontSize: 14, color: '#1e293b' },
  timestamp: { fontSize: 11, color: '#6b7280' },
  phaseHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    marginBottom: 6, padding: '0 2px',
  },
  phaseSub: { fontSize: 10, color: '#94a3b8', fontStyle: 'italic' },
  callList: { display: 'flex', flexDirection: 'column', gap: 4 },
  btnPush: {
    padding: '7px 16px', borderRadius: 7, border: '1px solid #3b82f6',
    background: '#eff6ff', color: '#1e40af', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  },
  btnRetry: {
    padding: '7px 16px', borderRadius: 7, border: '1px solid #f59e0b',
    background: '#fffbeb', color: '#92400e', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  },
};

const styles = {
  panel: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: '#f8fafc',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '24px 28px', borderBottom: '1px solid #e2e8f0', flexShrink: 0,
    background: '#ffffff',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  avatar: {
    width: '52px', height: '52px', borderRadius: '12px',
    background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '18px', fontWeight: '700',
    fontFamily: "'JetBrains Mono', monospace",
    boxShadow: '0 4px 14px rgba(37,99,235,0.2)',
  },
  name: {
    fontSize: '22px', fontWeight: '700', color: '#0f172a',
    fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.5px',
  },
  meta: { display: 'flex', alignItems: 'center', marginTop: '5px', gap: '8px' },
  closeBtn: {
    background: 'none', border: '1px solid #e2e8f0', fontSize: '18px', color: '#94a3b8',
    cursor: 'pointer', padding: '6px 10px', lineHeight: 1,
    borderRadius: '8px', transition: 'all 0.2s',
  },
  tabRow: {
    display: 'flex', gap: '4px', padding: '12px 24px',
    flexShrink: 0, background: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
  },
  tabWrapper: {
    display: 'flex', gap: '4px', flex: 1,
    background: '#f1f5f9', borderRadius: '14px', padding: '5px',
  },
  tab: {
    flex: 1, padding: '11px 8px', background: 'transparent', border: 'none',
    fontSize: '13px', fontWeight: '600', color: '#94a3b8', cursor: 'pointer',
    borderRadius: '10px', transition: 'all 0.25s ease', textAlign: 'center',
  },
  tabActive: {
    color: '#ffffff', background: '#2563eb',
    boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
  },
  content: {
    flex: 1, overflowY: 'auto', padding: '24px 28px'
  },
  detailGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    background: '#ffffff', borderRadius: '14px',
    border: '1px solid #e2e8f0', overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
  },
  detailRow: {
    display: 'flex', flexDirection: 'column',
    padding: '16px 20px', borderBottom: '1px solid #e2e8f0',
  },
  detailLabel: {
    fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.2px',
    color: '#94a3b8', marginBottom: '4px', fontWeight: '600',
  },
  detailValue: { fontSize: '14px', color: '#0f172a', fontWeight: '600' }
};

export default AppointmentDetailPanel;
